import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { MeterBarChartPlotArea } from './components/MeterBarChartPlotArea.js';
import { MeterBarChartThresholdLegend } from './components/MeterBarChartThresholdLegend/MeterBarChartThresholdLegend.js';
import { METER_BAR_CHART_ARIA_LABEL } from './constants.js';
import { useMeterBarChartConfig } from './hooks/useMeterBarChartConfig.js';
import { MeterBarChartProviders } from './providers/MeterBarChartProviders.js';
import { Icon } from './slots/Icon.js';
import { Label } from './slots/Label.js';
import { Max } from './slots/Max.js';
import { Min } from './slots/Min.js';
import { Threshold } from './slots/Threshold.js';
import { ThresholdLegend } from './slots/ThresholdLegend.js';
import { Tooltip } from './slots/Tooltip.js';
import { Value } from './slots/Value.js';
import type { MeterBarChartConsumerConfig } from './types/meter-bar-chart-config.js';
import { type MeterBarChartInternalConfig } from './types/meter-bar-chart-internal.js';
import { type MeterBarChartProps } from './types/meter-bar-chart.js';
import { buildThresholds } from './utils/build-thresholds.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../../core/styles/defaultChartValues.sty.js';
import { useObserveContainerSize } from '../../core/hooks/useObserveContainerSize.js';
import { ChartType } from '../../core/types/chart-types.js';
import type { ComponentWithName } from '../../core/types/component-with-name.js';
import { MeterBarChartFormattedValue } from '../shared/components/formatted-value/MeterBarChartFormattedValue.js';
import { MeterBarLayout } from '../shared/components/meter-bar-layout/MeterBarLayout.js';
import { LEGEND_VERTICAL_RATIO } from '../shared/constants.js';
import type { MeterBarChartElementRef } from '../shared/types/meter-bars.js';

/**
 * Single meter bar chart component */
const MeterBarChartRoot = forwardRef<
  MeterBarChartElementRef,
  PropsWithChildren<MeterBarChartProps>
>((props, forwardedRef) => {
  const {
    value,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'single-meter-bar-chart-root',
  } = props;

  const config: MeterBarChartInternalConfig = useMeterBarChartConfig(props);
  const {
    size,
    thresholds,
    min,
    max,
    minSlot,
    maxSlot,
    iconSlot,
    valueSlot,
    labelSlot,
    tooltip,
    formatter,
  } = config;

  const meterBarChartRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => {
    let exposedRef = {} as MeterBarChartElementRef;

    if (meterBarChartRef.current) {
      const consumerConfig: MeterBarChartConsumerConfig = {
        thresholds,
        size,
        min,
        max,
        formatter,
        tooltip,
      };

      exposedRef = {
        element: meterBarChartRef.current,
        getConfig: () =>
          JSON.stringify({ type: ChartType.SingleMeterBar, ...consumerConfig }),
      };
    }

    return exposedRef;
  });

  const [{ width, wrapperHeight }, containerRef] = useObserveContainerSize();
  const ref = useMergeRefs([containerRef, meterBarChartRef]);

  const { matchingThreshold, thresholdIndicators, legendItems } =
    buildThresholds(value, min, max, width, thresholds);

  const hasLegend = thresholds.legend && legendItems.length;

  return (
    <MeterBarChartProviders
      containerWidth={width}
      matchingThresholdColor={matchingThreshold?.color}
      meterBarChartInternal={config}
      meterBarChartProps={props}
      thresholdIndicators={thresholdIndicators}
    >
      <MeterBarLayout
        ref={ref}
        size={size}
        className={clsx(defaultChartSize, className)}
        style={style}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-testid={dataTestId}
        aria-label={METER_BAR_CHART_ARIA_LABEL}
      >
        {iconSlot && <MeterBarLayout.Icon>{iconSlot}</MeterBarLayout.Icon>}
        {labelSlot && <MeterBarLayout.Label>{labelSlot}</MeterBarLayout.Label>}
        {valueSlot && (
          <MeterBarLayout.Value>
            <MeterBarChartFormattedValue
              value={value}
              total={max}
              auto={valueSlot?.auto}
            >
              {valueSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Value>
        )}
        <MeterBarLayout.Chart>
          <MeterBarChartPlotArea />
        </MeterBarLayout.Chart>
        {minSlot && (
          <MeterBarLayout.Min>
            <MeterBarChartFormattedValue value={min} auto={minSlot?.auto}>
              {minSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Min>
        )}
        {maxSlot && (
          <MeterBarLayout.Max>
            <MeterBarChartFormattedValue value={max} auto={maxSlot?.auto}>
              {maxSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Max>
        )}
        {hasLegend && (
          <MeterBarLayout.Legend>
            <MeterBarChartThresholdLegend
              data={legendItems}
              legendSize={{
                width,
                height: wrapperHeight * LEGEND_VERTICAL_RATIO,
              }}
            />
          </MeterBarLayout.Legend>
        )}
      </MeterBarLayout>
    </MeterBarChartProviders>
  );
});

(MeterBarChartRoot as ComponentWithName<typeof MeterBarChartRoot>).displayName =
  'MeterBarChart';

/**
 * The `MeterBarChart` provides a lightweight and simple visualization progress
 * toward a completion, consumption of a total, etc.  It consists of a horizontal
 * bar that gradually fills up with color or shading as it approaches the max value.
 * @public
 */
export const MeterBarChart = Object.assign(MeterBarChartRoot, {
  Value,
  Label,
  Icon,
  Min,
  Max,
  Threshold,
  ThresholdLegend,
  Tooltip,
});
